import { useMemo } from 'react';

import { useCurrentUser } from '../../../shared/auth';
import { useTagsQuery } from '../../../shared/data/tags/useTagsQuery';

/**
 * The tag-group names flagged mandatory-for-questions for the current user's
 * business unit + program, derived from the tag catalog. Feeds the question
 * config validator's all-mandatory-tags rule (via {@link useReviewWizard}).
 *
 * BU + program scoped (matching the manual question flow and
 * `QuestionConfigBasicInformation`); the query stays idle until both are
 * present, so this returns `[]` until the catalog resolves — which is the safe
 * default (the mandatory rule can't false-positive before the catalog loads).
 * The result is memoized for a stable identity across renders (the wizard's
 * config memo + publish gate both key on it).
 */
export function useMandatoryTagGroups(): readonly string[] {
  const { businessUnit, programId } = useCurrentUser();
  const businessUnitId = businessUnit ?? '';
  const { data: tagsData } = useTagsQuery(
    { businessUnitId, programId, isActive: true },
    { enabled: !!businessUnitId && !!programId },
  );
  return useMemo<readonly string[]>(
    () =>
      (tagsData?.tags ?? [])
        .filter((tag) => tag.isMandatoryForQuestions && tag.tagGroupName)
        .map((tag) => tag.tagGroupName ?? ''),
    [tagsData],
  );
}
